import React, { useMemo } from 'react';

import {
  calculateInvestmentDistributionAmount,
  calculateInvestmentReinvestPricePerUnit,
  getInvestmentReinvestAmountLabelKey,
} from 'app-components/forms/investment/investmentTransactionFormDisplay';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';

import QAmountField from 'components/QAmountField';

import { investmentTransactionEditFormHooks } from 'components/Forms/InvestmentTransactionEditForm/investmentTransactionEditFormHooks';
import { amountLabelByKey } from './labels';
import { SecurityAndTagsFields } from './SecurityAndTagsFields';
import { handleNumericKeyDown, numberFromInput, useNumericTextField } from './utils';

export function ReinvestFields(): React.JSX.Element {
  const investmentTxnType = investmentTransactionEditFormHooks.useField('investmentTxnType');
  const units = investmentTransactionEditFormHooks.useField('units');
  const unitsInput = useNumericTextField({ value: units.value, onValueChange: units.onValueChange });
  const commission = investmentTransactionEditFormHooks.useField('commission');
  const costBasis = investmentTransactionEditFormHooks.useField('costBasis');
  const distributionAmount = useMemo(
    () => calculateInvestmentDistributionAmount({ commission: commission.value, costBasis: costBasis.value }),
    [commission.value, costBasis.value],
  );
  const pricePerUnit = useMemo(
    () => calculateInvestmentReinvestPricePerUnit({ distributionAmount, units: units.value }),
    [distributionAmount, units.value],
  );
  const label = amountLabelByKey[getInvestmentReinvestAmountLabelKey(investmentTxnType.value)];

  return (
    <>
      <SecurityAndTagsFields />
      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='investment-amount'
          editable
          disabled
          label='Amount'
          sx={{ minWidth: 110 }}
          noNegatives
          value={Math.abs(distributionAmount ?? 0)}
        />
        /
        <TextField
          id='units'
          label='Number of shares'
          type='number'
          inputProps={{ min: 1, inputMode: 'decimal' }}
          sx={{ minWidth: 110 }}
          value={unitsInput.value}
          onKeyDown={handleNumericKeyDown}
          onChange={unitsInput.onChange}
          error={units.error}
          helperText={units.helperText}
        />
        =
        <QAmountField
          id='price-per-share'
          editable
          disabled
          label='Price per share'
          sx={{ minWidth: 110 }}
          expectedSign='positive'
          value={pricePerUnit}
        />
      </Box>
      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='commission'
          editable
          label='Commission'
          sx={{ minWidth: 110 }}
          value={commission.value ?? undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            commission.onValueChange(numberFromInput(event.target.value))
          }
          error={commission.error}
          helperText={commission.helperText}
        />
      </Box>
      <Divider sx={{ maxWidth: 320 }} />
      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='distribution-amount'
          editable
          label={label}
          sx={{ minWidth: 110 }}
          noNegatives
          value={Math.abs(costBasis.value ?? 0)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            costBasis.onValueChange(numberFromInput(event.target.value))
          }
          error={costBasis.error}
          helperText={costBasis.helperText}
        />
      </Box>
    </>
  );
}
